import React, { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { profilingEndpoints, ITestingProtocol } from "../../endpoints/profiling";
import { Paginated } from "../../typings/Paginated";
import { ConfiguraTestModal } from "./ConfiguraTestModal";

/**
 * #8 Profiling Hub atleta — 14 card P1-P14 raggruppate in 2 cluster.
 * Tutti i 14 protocolli compute attivi backend (M5a complete).
 */
const MVP_ENABLED = new Set([
  "P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8",
  "P9", "P10", "P11", "P12", "P13", "P14",
]);

const CLUSTERS = [
  {
    key: "fv",
    title: "Forza · Velocità",
    color: "green",
    protocols: ["P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8"],
  },
  {
    key: "endurance",
    title: "Resistenza",
    color: "blue",
    protocols: ["P9", "P10", "P11", "P12", "P13", "P14"],
  },
];

const PROTOCOL_DESCRIPTIONS: Record<string, string> = {
  P1: "Profilo load-velocity arti inferiori (squat, deadlift, hip thrust)",
  P2: "Profilo load-velocity arti superiori (bench, OHP, pull-up)",
  P3: "Squat Jump incrementale → F0/V0/Pmax (Samozino)",
  P4: "Sprint distance-time → FH0/VH0/Pmax/DRF",
  P5: "Sled push/pull → Lopt (carico ottimale potenza)",
  P6: "Drop Jump multi-altezza → RSI ottimale + altezza ottimale",
  P7: "Isometric Mid-Thigh Pull → PF + RFD + DSI vs CMJ",
  P8: "Nordic Hamstring → Peak Force + asimmetria + break angle",
  P9: "Step lattato → LT1, LT2, zone HR aerobiche",
  P10: "VAMEVAL → VMA continua + zone %VMA",
  P11: "Gacon 45-15 → VMA intermittente",
  P12: "Yo-Yo IR1 → distanza + VO2max stimato",
  P13: "Yo-Yo IR2 → versione intensa",
  P14: "30-15 IFT → VIFT + zone HIIT",
};

export const ProfilingHubPage: React.FC = () => {
  const { athleteId } = useParams<{ athleteId: string }>();
  const qc = useQueryClient();
  const [showConfigModal, setShowConfigModal] = useState(false);

  const { data: protocolsResp, isLoading } = useQuery({
    queryKey: ["profiling-protocols", "profile"],
    // `type` non è supportato come filtro server-side da listProtocols
    // (accetta solo `exerciseId`): il campo veniva già ignorato a runtime.
    queryFn: () => profilingEndpoints.listProtocols().then((r) => r.data),
  });

  const { data: profilesResp } = useQuery({
    queryKey: ["athlete-profiles", athleteId],
    queryFn: () => profilingEndpoints.listAthleteProfiles(athleteId!, { limit: 100 }).then((r) => r.data),
    enabled: !!athleteId,
  });

  const { data: alertsResp } = useQuery({
    queryKey: ["auto-pr-alerts", athleteId],
    queryFn: () => profilingEndpoints.listAutoPRAlerts(athleteId).then((r) => r.data),
    enabled: !!athleteId,
  });

  const confirmPRMut = useMutation({
    mutationFn: (alertId: string) => profilingEndpoints.confirmAutoPR(alertId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auto-pr-alerts"] });
      qc.invalidateQueries({ queryKey: ["exercise-history"] });
    },
  });

  const discardPRMut = useMutation({
    mutationFn: (alertId: string) => profilingEndpoints.discardAutoPR(alertId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auto-pr-alerts"] });
    },
  });

  const protocols: ITestingProtocol[] =
    (protocolsResp as Paginated<ITestingProtocol> | undefined)?.docs ??
    (protocolsResp as { data?: ITestingProtocol[] } | undefined)?.data ??
    (Array.isArray(protocolsResp) ? protocolsResp : []) ??
    [];
  const profiles = profilesResp ?? [];
  const autoPRAlerts: any[] = alertsResp?.docs ?? [];

  const protocolByHubId = useMemo(() => {
    const m = new Map<string, ITestingProtocol>();
    for (const p of protocols) if (p.profilingHubProtocolId) m.set(p.profilingHubProtocolId, p);
    return m;
  }, [protocols]);

  const lastProfileByProtocolId = useMemo(() => {
    const m = new Map<string, any>();
    for (const p of profiles) {
      const cur = m.get(p.protocolId);
      if (!cur || new Date(p.sessionDate) > new Date(cur.sessionDate)) m.set(p.protocolId, p);
    }
    return m;
  }, [profiles]);

  if (isLoading)
    return (
      <Container py={10}>
        <Spinner />
      </Container>
    );

  return (
    <Container maxW="container.xl" py={6}>
      <Flex justify="space-between" align="center" mb={2}>
        <Heading size="lg">Profiling Hub</Heading>
        <Button colorScheme="purple" onClick={() => setShowConfigModal(true)}>
          + Configura nuovo test
        </Button>
      </Flex>
      <Text color="gray.500" mb={6} fontSize="sm">
        14 protocolli generano profilo/curva. Tutti P1-P14 compute attivo backend.
      </Text>

      {autoPRAlerts.length > 0 && (
        <Box bg="blue.50" border="1px solid" borderColor="blue.300" borderRadius="lg" p={4} mb={6}>
          <Heading size="sm" color="blue.800" mb={3}>
            ⚡ {autoPRAlerts.length} possibili nuovi PR rilevati da training
          </Heading>
          <Stack gap={2}>
            {autoPRAlerts.map((a) => (
              <Flex key={a._id} justify="space-between" align="center" bg="white" p={3} borderRadius="md" border="1px solid" borderColor="blue.200">
                <Box flex={1}>
                  <Text fontSize="sm" fontWeight={600}>{a.message}</Text>
                  <Text fontSize="xs" color="gray.500">
                    {a.metadata?.protocolName} · loadActual {a.metadata?.loadActual} kg vs PR {a.metadata?.previousPR} kg
                  </Text>
                </Box>
                <HStack>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="gray"
                    disabled={discardPRMut.isPending}
                    onClick={() => discardPRMut.mutate(a._id)}
                  >
                    {discardPRMut.isPending && discardPRMut.variables === a._id ? <Spinner size="xs" /> : "Scarta"}
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="green"
                    disabled={confirmPRMut.isPending}
                    onClick={() => confirmPRMut.mutate(a._id)}
                  >
                    {confirmPRMut.isPending && confirmPRMut.variables === a._id ? <Spinner size="xs" /> : "Conferma PR"}
                  </Button>
                </HStack>
              </Flex>
            ))}
          </Stack>
        </Box>
      )}

      <ConfiguraTestModal open={showConfigModal} onClose={() => setShowConfigModal(false)} athleteId={athleteId!} />

      <Stack gap={8}>
        {CLUSTERS.map((cluster) => (
          <Box key={cluster.key}>
            <Heading size="sm" color={`${cluster.color}.700`} mb={3} textTransform="uppercase" letterSpacing="wide">
              {cluster.title}
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
              {cluster.protocols.map((protoId) => {
                const protocol = protocolByHubId.get(protoId);
                const isEnabled = MVP_ENABLED.has(protoId);
                const lastProfile = protocol ? lastProfileByProtocolId.get(protocol._id) : undefined;
                return (
                  <ProtocolCard
                    key={protoId}
                    protoId={protoId}
                    protocol={protocol}
                    isEnabled={isEnabled}
                    lastProfile={lastProfile}
                    athleteId={athleteId!}
                    color={cluster.color}
                  />
                );
              })}
            </SimpleGrid>
          </Box>
        ))}
      </Stack>
    </Container>
  );
};

const ProtocolCard: React.FC<{
  protoId: string;
  protocol?: ITestingProtocol;
  isEnabled: boolean;
  lastProfile?: any;
  athleteId: string;
  color: string;
}> = ({ protoId, protocol, isEnabled, lastProfile, athleteId, color }) => {
  const card = (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      p={4}
      opacity={isEnabled ? 1 : 0.5}
      cursor={isEnabled ? "pointer" : "not-allowed"}
      _hover={isEnabled ? { borderColor: `${color}.400`, shadow: "md" } : undefined}
      h="100%"
    >
      <Flex justify="space-between" align="start" mb={2}>
        <Box bg={`${color}.500`} color="white" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="800" fontFamily="mono">
          {protoId}
        </Box>
        {isEnabled ? (
          <Box bg="green.50" color="green.700" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="700">
            ATTIVO
          </Box>
        ) : (
          <Box bg="gray.100" color="gray.500" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="700">
            IN ARRIVO
          </Box>
        )}
      </Flex>
      <Text fontWeight={700} fontSize="sm" mb={1}>
        {protocol?.name?.replace(/^P\d+\s*[—-]\s*/, "") || "—"}
      </Text>
      <Text fontSize="xs" color="gray.500" mb={3}>
        {PROTOCOL_DESCRIPTIONS[protoId]}
      </Text>
      {lastProfile ? (
        <HStack gap={4} fontSize="xs" pt={2} borderTop="1px solid" borderColor="gray.100">
          <Box>
            <Text color="gray.400" textTransform="uppercase">Ultimo</Text>
            <Text fontFamily="mono" fontWeight={600}>{new Date(lastProfile.sessionDate).toLocaleDateString("it-IT")}</Text>
          </Box>
          {Object.entries(lastProfile.outputs || {})
            .filter(([k, v]) => typeof v === "number" && !["n", "intercept", "slope"].includes(k))
            .slice(0, 2)
            .map(([k, v]) => (
              <Box key={k}>
                <Text color="gray.400" textTransform="uppercase">{k}</Text>
                <Text fontFamily="mono" fontWeight={600}>{String(v)}</Text>
              </Box>
            ))}
        </HStack>
      ) : (
        isEnabled && (
          <Text fontSize="xs" color="gray.400" pt={2} borderTop="1px solid" borderColor="gray.100">
            Nessun profilo registrato
          </Text>
        )
      )}
    </Box>
  );

  if (!isEnabled || !protocol) return card;
  return <Link to={`/dashboard/users/${athleteId}/profiling/${protocol._id}`}>{card}</Link>;
};

export default ProfilingHubPage;
